export type CookieConsent = "accepted" | "rejected" | null;

export interface RankingPreferences {
  countryCode: string;
  categoryId: string;
}

export const COOKIE_CONSENT_CHANGED_EVENT = "autovid-cookie-consent-changed";

const CONSENT_COOKIE = "autovid_cookie_consent";
const LANGUAGE_COOKIE = "autovid_language";
const RANKING_COOKIE = "autovid_ranking_preferences";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${name}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const value = cookie.trim();
    if (value.startsWith(prefix)) {
      return decodeURIComponent(value.substring(prefix.length));
    }
  }

  return null;
}

function writeCookie(name: string, value: string, maxAge = COOKIE_MAX_AGE): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`;
}

function deleteCookie(name: string): void {
  writeCookie(name, "", 0);
}

export function getCookieConsent(): CookieConsent {
  const consent = readCookie(CONSENT_COOKIE);
  return consent === "accepted" || consent === "rejected" ? consent : null;
}

export function setCookieConsent(consent: Exclude<CookieConsent, null>): void {
  writeCookie(CONSENT_COOKIE, consent);

  if (consent === "rejected") {
    deleteCookie(LANGUAGE_COOKIE);
    deleteCookie(RANKING_COOKIE);
  }

  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: consent }));
}

export function saveLanguagePreference(language: string): void {
  if (getCookieConsent() === "accepted") {
    writeCookie(LANGUAGE_COOKIE, language);
  }
}

export function getLanguagePreference(): string | null {
  return getCookieConsent() === "accepted" ? readCookie(LANGUAGE_COOKIE) : null;
}

export function saveRankingPreferences(preferences: RankingPreferences): void {
  if (getCookieConsent() === "accepted") {
    writeCookie(RANKING_COOKIE, JSON.stringify(preferences));
  }
}

export function getRankingPreferences(): RankingPreferences | null {
  if (getCookieConsent() !== "accepted") {
    return null;
  }

  const savedValue = readCookie(RANKING_COOKIE);
  if (!savedValue) {
    return null;
  }

  try {
    return JSON.parse(savedValue) as RankingPreferences;
  } catch {
    deleteCookie(RANKING_COOKIE);
    return null;
  }
}
